import { Controller } from 'stimulus';
import addressResolver from '../helpers/addressResolver';
import debounce from '../helpers/debounce';

/**
 *  Autofill address based on postal code and house number
 *
 * @class AddressAutofill
 * @extends {Controller}
 */
class AddressAutofill extends Controller {
  connect() {
    this.POSTAL_LENGTH = 6;
    this.HOUSE_NUMBER_LENGTH = 1;

    this.debouncedCheck = debounce(this.runCheck.bind(this), 20);

    if (this.postalCodeTarget.value !== '' && this.houseNumberTarget.value !== '') {
      this.togglePreviewFields(false);
    }
  }

  onBlur() {
    this.debouncedCheck();
  }

  onCountryChange(event) {
    const currentTarget = event.target;

    this.postalCodeTarget.value = '';
    this.houseNumberTarget.value = '';
    this.streetTarget.value = '';
    this.cityTarget.value = '';

    this.toggleStreetCityFields(true, currentTarget.value === 'NL');
    this.togglePreviewFields(true);
  }

  /**
   * Small wrapper around the API call, this will handle errors and loading states
   *
   * @returns {Void}
   * @memberof AddressAutofill
   */
  runCheck() {
    const postal = this.postalCodeTarget.value.trim().replace(' ', '');
    const houseNumber = this.houseNumberTarget.value.trim().replace(' ', '');
    const country = 'nl';

    if (postal.length !== this.POSTAL_LENGTH || houseNumber.length < this.HOUSE_NUMBER_LENGTH) {
      return;
    }

    addressResolver(postal, houseNumber, country)
      .then((data) => {
        this.outputData(data);
      })
      .catch(() => {
        this.toggleStreetCityFields(true, false);
      });
  }

  /**
   * Toggle preview fields
   *
   * @param state Boolean
   * @memberof AddressAutofill
   */
  togglePreviewFields(state) {
    this.fieldsPreviewTarget.hidden = state;
  }

  /**
   * Toggle street and city fields manually
   *
   * @param fieldsPreviewState Boolean
   * @param streetCityFieldsState Boolean
   * @memberof AddressAutofill
   */
  toggleStreetCityFields(fieldsPreviewState = true, streetCityFieldsState = false) {
    this.fieldsPreviewTarget.hidden = fieldsPreviewState;
    this.streetCityFieldsTarget.hidden = streetCityFieldsState;
  }

  /**
   * Output data to the DOM, but only if the input fields are still empty
   *
   * @param  {Object} data API object with all address information
   * @return {Void}
   * @memberof AddressAutofill
   */
  outputData(data) {
    if (data.length === 0) {
      this.streetTarget.value = '';
      this.streetPreviewTarget.innerHTML = '';

      this.cityTarget.value = '';
      this.cityPreviewTarget.innerHTML = '';

      this.streetPreviewTarget.innerHTML = window.app.i18n.addressNotFound;
      this.fieldsPreviewTarget.hidden = false;

      return;
    }

    this.streetTarget.value = data.street;
    this.streetPreviewTarget.innerHTML = `${data.street}, `;

    this.cityTarget.value = data.city;
    this.cityPreviewTarget.innerHTML = data.city;

    this.fieldsPreviewTarget.hidden = false;
  }
}

AddressAutofill.targets = [
  'postalCode',
  'houseNumber',
  'street',
  'city',
  'fieldsPreview',
  'streetPreview',
  'cityPreview',
  'streetCityFields',
];

export default AddressAutofill;
